import { Theme } from '@mui/material';
import React from 'react';
import SwiperCore, { Navigation, Scrollbar } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.min.css';
import { makeStyles } from 'tss-react/mui';

const navButtonCommon = (theme: Theme) => ({
	background: '#4a4a4a',
	width: 55,
	height: 55,
	top: 76,
	marginTop: 0,
	[theme.breakpoints.up('lg')]: {
		top: '50%',
		transform: 'translateY(-50%)'
	},
	'&::after': {
		fontSize: '24px',
		color: '#ffffff'
	}
});

SwiperCore.use([Navigation, Scrollbar]);

type StylesProps = { activeSlideEffects: boolean, noScrollbar: boolean };

const useStyles = makeStyles<StylesProps>({ name: 'Swiper' })((theme, { activeSlideEffects, noScrollbar }) => ({
	root: {
		'&.swiper-container-horizontal': {
			overflow: 'visible',
			paddingBottom: noScrollbar ? 0 : 30,
			'& .swiper-wrapper': {
				transition: 'all 500ms ease 0s !important'
			},
			'& .swiper-scrollbar': {
				bottom: 0,
				left: '50%',
				transform: 'translateX(-50%)',
				width: 130,
				height: 10,
				background: 'rgba(0,0,0,0.03)',
				'& .swiper-scrollbar-drag': {
					background: theme.palette.scrollbar.dark
				}
			}
		},
		'& .swiper-button-prev': {
			...navButtonCommon(theme),
			left: 0
		},
		'& .swiper-button-next': {
			...navButtonCommon(theme),
			right: 0
		}
	},
	slide: {
		'&.swiper-slide': {
			height: 'unset',
			transition: 'all 0.5s',
			opacity: activeSlideEffects ? 0.8 : 1,
			transform: activeSlideEffects ? 'scaleY(0.968) !important' : 'unset',
			'&.swiper-slide-active': {
				opacity: 1,
				transform: 'scaleY(1) !important',
				boxShadow: activeSlideEffects ? '0px 4px 16px rgba(0, 0, 0, 0.15)' : 'none'
			}
		}
	}
}));

export type SkodaSwiperProps = React.ComponentProps<typeof Swiper> & {
	children: React.ReactElement[];
	activeSlideEffects?: boolean;
	classes?: Partial<ReturnType<typeof useStyles>['classes']>
};

export const SkodaSwiper: React.FC<SkodaSwiperProps> = (props) => {
	const { children, className, classes, activeSlideEffects, ...other } = props;

	let { classes: styles, cx } = useStyles({ activeSlideEffects, noScrollbar: props.scrollbar === false }, { props: { classes } });

	const slides = children.map(item =>
		<SwiperSlide key={item.key} className={cx(styles.slide, classes?.slide)}>{item}</SwiperSlide>);

	return (
		<Swiper
			className={cx(styles.root, className)}
			scrollbar={{ draggable: true }}
			{...other}>
			{slides}
		</Swiper>
	);
};

SkodaSwiper.defaultProps = {
	activeSlideEffects: true,
	spaceBetween: 10
} as SkodaSwiperProps;
